import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usePartyFilter } from '../context/PartyFilterContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  CreditCard, 
  Building2, 
  TrendingUp, 
  Users, 
  LogOut,
  Menu,
  X,
  Globe,
  FileText,
  Briefcase,
  Filter,
  XCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_investdash-70/artifacts/pagenf71_brandmark-design%20%284%29.svg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { selectedPartyId, setSelectedPartyId, resetFilter, isFiltered } = usePartyFilter();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await axios.get(`${API}/parties`, { withCredentials: true });
        setParties(res.data);
      } catch (error) {
        console.error('Error fetching parties:', error);
      }
    };
    if (user) {
      fetchParties();
    }
  }, [user]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/loans', icon: CreditCard, label: t('loans') },
    { path: '/realestate', icon: Building2, label: t('realEstate') },
    { path: '/parties', icon: Briefcase, label: t('parties') },
    { path: '/credit-application', icon: FileText, label: t('creditApplication') },
    { path: '/cashflow', icon: TrendingUp, label: t('cashflow') },
  ];

  if (isAdmin()) {
    navItems.push({ path: '/users', icon: Users, label: t('users') });
  }

  const isActive = (path) => location.pathname === path;
  const selectedParty = parties.find(p => p.party_id === selectedPartyId);

  return (
    <div className="flex min-h-screen bg-[#0f172a]">
      {/* Mobile header bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-[#1e293b] border-b border-slate-700 flex items-center justify-between px-4 mobile-safe-top">
        <button
          className="p-2 -ml-2 rounded-lg text-white hover:bg-slate-700 active:bg-slate-600 transition-colors"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
          aria-label="Menu"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="BRiQCAP" className="h-8 w-auto" />
          <span className="text-white font-['Comfortaa'] font-bold">BRiQCAP</span>
        </Link>
        
        <button
          onClick={toggleLanguage}
          className="p-2 -mr-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-white"
          aria-label="Toggle language"
        >
          <Globe size={20} />
        </button>
      </header>

      {/* Sidebar */}
      <aside 
        id="mobile-sidebar"
        style={{
          position: 'fixed',
          top: '3.5rem',
          left: 0,
          width: '18rem',
          height: 'calc(100% - 3.5rem)',
          backgroundColor: '#1e293b',
          borderRight: '1px solid #334155',
          zIndex: 40,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out'
        }}
        className="lg:!static lg:!top-0 lg:!h-full lg:!w-64 lg:!transform-none"
      >
        <div className="flex flex-col h-full lg:pt-0">
          {/* Logo - hidden on mobile (shown in header) */}
          <div className="hidden lg:block p-6 border-b border-slate-800">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="BRiQCAP" className="h-10 w-auto" />
              <div className="flex flex-col">
                <span className="text-lg font-bold text-white font-['Comfortaa'] leading-tight">BRiQCAP</span>
                <span className="text-xs text-[#b4976f] font-['Comfortaa']">FAAS Portal</span>
              </div>
            </Link>
          </div>

          {/* Global Party Filter */}
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-400">{t('filterByParty')}</span>
            </div>
            <div className="flex gap-2">
              <Select value={selectedPartyId} onValueChange={setSelectedPartyId}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white text-sm flex-1 min-h-[44px]" data-testid="global-party-filter">
                  <SelectValue placeholder={t('allParties')} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-64">
                  <SelectItem value="all" className="text-white hover:bg-slate-800 min-h-[44px]">
                    {t('allParties')}
                  </SelectItem>
                  {parties.map((party) => (
                    <SelectItem key={party.party_id} value={party.party_id} className="text-white hover:bg-slate-800 min-h-[44px]">
                      {party.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isFiltered && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetFilter}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 px-3 min-h-[44px]"
                  title={t('resetFilter')}
                >
                  <XCircle className="h-5 w-5" />
                </Button>
              )}
            </div>
            {isFiltered && selectedParty && (
              <p className="text-xs text-amber-400 mt-2">
                Filtering: {selectedParty.name}
              </p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link flex items-center gap-3 min-h-[48px] ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon size={22} />
                <span className="text-base">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800 mobile-safe-bottom">
            {/* Language toggle - hidden on mobile (shown in header) */}
            <button
              onClick={toggleLanguage}
              className="hidden lg:flex w-full items-center gap-3 px-4 py-3 mb-3 rounded-md text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
              data-testid="language-toggle"
            >
              <Globe size={20} />
              <span>{language === 'nl' ? 'English' : 'Nederlands'}</span>
            </button>

            {/* User info */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800/50">
              <Avatar className="h-10 w-10 flex-shrink-0">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#b4976f] text-white">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                <span className={`inline-block mt-1 ${user?.role === 'admin' ? 'badge-admin' : 'badge-investor'}`}>
                  {user?.role === 'admin' ? t('admin') : t('investor')}
                </span>
              </div>
            </div>

            {/* Logout button */}
            <Button
              variant="ghost"
              className="w-full mt-3 justify-start text-slate-400 hover:text-white hover:bg-slate-800 min-h-[48px]"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={20} className="mr-3" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 top-14 bg-black/60 z-30 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-0 min-h-screen overflow-auto pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
